import { createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import equalityFn from 'zustand/shallow';
import { IS_DEVELOPMENT } from '~/modules/config';
import { Routes } from '~/modules/routes';
import { ListingResponse } from '~/modules/services/listing_service';
import { handleException } from '~/modules/utilities/error_utilities';
import { Marketplace } from '~/modules/utilities/language_utils';
import { selectExtensionListing } from '~/modules/utilities/listing_utils';
import { getPublisherListings } from '../services/moreFromProviderService';
import { listingStore } from './listingStore';
import { routerStore, selectMatch } from './routerStore';

export const DEFAULT_PROVIDER_LISTINGS_TO_DISPLAY = 4;
export const MIN_PROVIDER_LISTINGS_TO_DISPLAY = 1;

export enum MoreFromThisProviderLoadingStatus {
    UNINITILIAZED = 'UNINITILIAZED',
    LOADED = 'LOADED',
    LOADING = 'LOADING',
    ERROR = 'ERROR',
}
export interface MoreFromProviderState extends State {
    loadingStatus: MoreFromThisProviderLoadingStatus;
    data: {
        listings: ListingResponse[];
        publisherId: string;
    };
    listingId: string;
}

const initialState: MoreFromProviderState = {
    loadingStatus: MoreFromThisProviderLoadingStatus.UNINITILIAZED,
    data: {
        listings: [],
        publisherId: '',
    },
    listingId: '',
};

export const moreFromProviderStore = createStore(
    initialState,

    //(set: SetState<MoreFromProviderState>, get: GetState<MoreFromProviderState>) => ({
    (set: SetState<MoreFromProviderState>, _get: GetState<MoreFromProviderState>) => ({
        async fetchProviderListings(listingId: string, publisherId: string, marketplace?: Marketplace) {
            try {
                set(function setLoading(draft) {
                    draft.loadingStatus = MoreFromThisProviderLoadingStatus.LOADING;
                    draft.listingId = listingId;
                });

                const data = await getPublisherListings(publisherId, listingId, marketplace);

                set(function loadListings(draft: MoreFromProviderState) {
                    draft.data.listings = data;
                    draft.data.publisherId = publisherId;
                    draft.loadingStatus = MoreFromThisProviderLoadingStatus.LOADED;
                });
            } catch (error) {
                handleException(error, 'moreFromProviderStore.fetchProviderListings', { listingId, publisherId });
                set(function failedLoadListings(draft: MoreFromProviderState) {
                    draft.loadingStatus = MoreFromThisProviderLoadingStatus.ERROR;
                });
            }
        },
        reset() {
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    'moreFromProviderStore',
    IS_DEVELOPMENT,
);

function checkListingId() {
    const { params, url } = selectMatch(routerStore.getState());

    if (Routes.LISTING !== url) {
        moreFromProviderStore.reset();
        return;
    }

    // @ts-expect-error * Automated Type Error Fix
    if (!params.listingId) {
        return;
    }

    // @ts-expect-error * Automated Type Error Fix
    const listing = listingStore.getState().listings.get(params.listingId);
    const listingId = listing?.external_id;
    const publisherId = listing?.publisher?.external_id;

    if (!publisherId || !listingId) {
        return;
    }

    const moreFromProviderState = moreFromProviderStore.getState();
    if (
        moreFromProviderState.loadingStatus !== MoreFromThisProviderLoadingStatus.UNINITILIAZED &&
        listingId === moreFromProviderState.listingId
    ) {
        return;
    }

    const extension = selectExtensionListing(listing);
    const marketplace: Marketplace = extension?.marketplace ?? 'English';
    moreFromProviderStore.fetchProviderListings(listingId, publisherId, marketplace);
}

routerStore.subscribe(checkListingId, selectMatch, equalityFn);
listingStore.subscribe(checkListingId);
checkListingId();
